import { useCallback, useEffect, useState } from "react";

import { api } from "./api.js";
import { Layout, type PageId } from "./components/layout.js";
import { LoadingState } from "./components/state.js";
import { DownloadsPage } from "./pages/downloads-page.js";
import { ErrorsPage } from "./pages/errors-page.js";
import { InstallationsPage } from "./pages/installations-page.js";
import { LoginPage } from "./pages/login-page.js";
import { OverviewPage } from "./pages/overview-page.js";
import { SystemPage } from "./pages/system-page.js";

const pageCopy: Record<PageId, { title: string }> = {
  overview: {
    title: "ОБЗОР"
  },
  errors: {
    title: "ОШИБКИ"
  },
  downloads: {
    title: "ЗАГРУЗКИ"
  },
  installations: {
    title: "КЛИЕНТЫ"
  },
  system: {
    title: "СИСТЕМА"
  }
};

function pageFromHash(): PageId {
  const candidate = window.location.hash.replace(/^#\/?/, "");
  return candidate in pageCopy ? (candidate as PageId) : "overview";
}

export function App() {
  const [page, setPage] = useState<PageId>(pageFromHash);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const [authenticationEnabled, setAuthenticationEnabled] = useState(false);
  const [initialFingerprint, setInitialFingerprint] = useState<string>();

  const checkAuth = useCallback(async () => {
    setCheckingAuth(true);
    try {
      const status = await api.authStatus();
      setAuthenticationEnabled(status.authentication_enabled);
      setAuthenticated(status.authenticated);
    } catch {
      setAuthenticationEnabled(true);
      setAuthenticated(false);
    } finally {
      setCheckingAuth(false);
    }
  }, []);

  useEffect(() => {
    void checkAuth();
    const onHashChange = (): void => setPage(pageFromHash());
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, [checkAuth]);

  const navigate = (target: PageId): void => {
    if (target !== "errors") {
      setInitialFingerprint(undefined);
    }
    window.location.hash = `/${target}`;
    setPage(target);
  };

  const openError = (fingerprint: string): void => {
    setInitialFingerprint(fingerprint);
    window.location.hash = "/errors";
    setPage("errors");
  };

  const unauthorized = useCallback(() => {
    setAuthenticationEnabled(true);
    setAuthenticated(false);
  }, []);

  const logout = async (): Promise<void> => {
    try {
      await api.logout();
    } finally {
      setAuthenticated(false);
    }
  };

  if (checkingAuth) {
    return (
      <main className="bootstrap-screen">
        <LoadingState label="ПРОВЕРКА ДОСТУПА" />
      </main>
    );
  }
  if (!authenticated) {
    return <LoginPage onSuccess={() => void checkAuth()} />;
  }

  const copy = pageCopy[page];
  return (
    <Layout
      page={page}
      title={copy.title}
      authenticationEnabled={authenticationEnabled}
      onNavigate={navigate}
      onLogout={() => void logout()}
    >
      {page === "overview" ? (
        <OverviewPage
          onUnauthorized={unauthorized}
          onOpenError={openError}
        />
      ) : page === "errors" ? (
        <ErrorsPage
          onUnauthorized={unauthorized}
          {...(initialFingerprint ? { initialFingerprint } : {})}
        />
      ) : page === "downloads" ? (
        <DownloadsPage onUnauthorized={unauthorized} />
      ) : page === "installations" ? (
        <InstallationsPage onUnauthorized={unauthorized} />
      ) : (
        <SystemPage onUnauthorized={unauthorized} />
      )}
    </Layout>
  );
}
